import { type ProfileInterface } from './types/profile.interface'
import { type CompanyInterface } from './types/company.interface'
import {
  type RegistrationDataThird,
  type RegistrationDataSecond,
  type RegistrationDataFirst
} from './types/registration/registration-get-data.types'
import { type RegistrationFormData } from './types/registration/registration-post-data.types'

import { fetchTransport } from 'util/core/transport/fetch.transport'
import { checkFetchError } from 'util/core/tool/check-fetch-error'
import { appConfig } from 'util/core/config/app-config.instance'
import { type RegistrationType } from 'util/search/constants/user'
import { type RegistrationLegalEntity } from './types/registration/legal-entity.types'

class DataAccessUser {
  public async getProfile (): Promise<ProfileInterface> {
    const response = await fetchTransport.get({
      api: 'USERS_API',
      url: '/api/users/profile'
    })
    checkFetchError(response.status)
    return response.json() as ProfileInterface
  }

  public async getCompany (): Promise<any> {
    const response = await fetchTransport.get({
      api: 'USERS_API',
      url: '/api/users/profile/company'
    })
    checkFetchError(response.status)
    return response.json() as CompanyInterface
  }

  public async getCountries (): Promise<any> {
    const response = await fetch(`${appConfig.get('USERS_API')}/lists/countries.json`)
    checkFetchError(response.status)
    return await response.json()
  }

  public async getСlassificationEconomicEntities (): Promise<any> {
    const response = await fetch(`${appConfig.get('USERS_API')}/lists/classificationEconomicEntities.json`)
    checkFetchError(response.status)
    return await response.json()
  }

  public async getCustomerCategory (): Promise<any> {
    const response = await fetch(`${appConfig.get('USERS_API')}/lists/customerCategory.json`)
    checkFetchError(response.status)
    return await response.json()
  }

  public async forgotPassword (email: string): Promise<any> {
    const response = await fetchTransport.post({
      url: '/api/users/forgot-password',
      api: 'USERS_API',
      data: {
        email
      }
    })

    if (response.status >= 400 && response.status < 600) {
      throw new Error(
        response.status.toString()
      )
    }

    const result = {
      ok: response.ok,
      response: await response.json()
    }
    return result
  }

  public async newPassword (recoveryCode: string, password: string): Promise<any> {
    const response = await fetchTransport.post({
      url: '/api/users/new-password',
      api: 'USERS_API',
      data: {
        recoveryCode, password
      }
    })

    if (response.status >= 400 && response.status < 600) {
      throw new Error(
        response.status.toString()
      )
    }

    const result = {
      ok: response.ok,
      response: await response.json()
    }
    return result
  }

  public async getRegistrationData (
    language: 'uk' | 'en',
    step: 1 | 2 | 3,
    userGroup: RegistrationType,
    countryId?: number,
    incorporationCountryId?: string
  ): Promise<RegistrationDataFirst | RegistrationDataSecond | RegistrationDataThird> {
    const response = await fetchTransport.post({
      api: 'USERS_API',
      url: `/api/users/registration/form-data/step-${step}?getData`, // Added "getData" flag for easier debugging in Network tab
      data: {
        languages: language,
        userGroup,
        countryId,
        incorporationCountryId
      }
    })

    checkFetchError(response.status)

    const json = await response.json()
    const result = json.data[language]

    if (countryId) {
      return result as RegistrationDataSecond
    }
    return result as RegistrationDataFirst
  }

  public async registerUser (data: RegistrationFormData, type: RegistrationType): Promise<any> {
    const response = await fetchTransport.post({
      api: 'USERS_API',
      url: `/api/users/registration/${type}`,
      data
    })

    checkFetchError(response.status, response)

    const result = await response.json()

    return result
  }

  public async getSmsCode (data: RegistrationFormData, type: RegistrationType): Promise<any> {
    const response = await fetchTransport.post({
      api: 'USERS_API',
      url: `/api/users/registration/${type}/sms-code`,
      data
    })

    checkFetchError(response.status, response)

    const result = await response.json()

    return result
  }

  public async validateRegistration (
    type: RegistrationType,
    step: number,
    data: RegistrationLegalEntity
  ): Promise<RegistrationLegalEntity> {
    const response = await fetchTransport.post({
      api: 'USERS_API',
      url: `/api/users/registration/${type}/validate/step-${step}?validation`, // Added "validation" flag for easier debugging in Network tab
      data
    })

    checkFetchError(response.status, response)

    const result = await response.json()

    return result
  }

  public async companyOpenDataBotLookup (identifier: string): Promise<any> {
    // const mokupData = '44487664'
    const response = await fetchTransport.post({
      api: 'USERS_API',
      url: '/api/company/lookup',
      data: {
        // identifier: mokupData
        identifier
      }
    })

    checkFetchError(response.status)

    const result = await response.json()

    return {
      ...result.data,
      ...(result.data.raw && { raw: JSON.parse(result.data.raw) })
    }
  }
}

export const dataAccessUser = new DataAccessUser()
